import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  Avatar,
  Button,
  TextField,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useTheme,
  CircularProgress,
  Alert,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import NotificationsIcon from '@mui/icons-material/Notifications';
import LockIcon from '@mui/icons-material/Lock';
import ChatIcon from '@mui/icons-material/Chat';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import { motion } from 'framer-motion';
import masterService from '../../services/masterService';
import { useAuth } from '../../context/AuthContext';

const GuestUserDashboard = () => {
  const theme = useTheme();
  const { user: currentUser, isLoading } = useAuth();

  // State for clock
  const [currentTime, setCurrentTime] = useState('Loading...');

  // State for modals
  const [showPurchasedModal, setShowPurchasedModal] = useState(false);
  const [showPaywallModal, setShowPaywallModal] = useState(false);
  const [showPaywallModal2, setShowPaywallModal2] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [showPremiumAccessModal, setShowPremiumAccessModal] = useState(false);

  // State for ratings
  const [ratings, setRatings] = useState({
    priya: 0,
    arjun: 0,
  });

  // State for search filters
  const [searchForm, setSearchForm] = useState({
    age_min: '',
    age_max: '',
    location: '',
    qualification: '',
    gender: '',
    country: '',
    state: '',
    city: '',
    caste: '',
    raasi: '',
    nakshatra: '',
    padam: '',
    marital_status: '',
    religion: '',
    occupation: '',
    income: '',
  });

  // Results and loading state
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState('');

  // Dropdown data
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [qualifications, setQualifications] = useState([]);
  const [raasis, setRaasis] = useState([]);
  const [nakshatras, setNakshatras] = useState([]);
  const [padams, setPadams] = useState([]);
  const [castes, setCastes] = useState([]);

  // State for interest count
  const [interestCount, setInterestCount] = useState(0);
  const INTEREST_LIMIT = 3;

  // State for shortlisted profiles
  const [shortlistedProfiles, setShortlistedProfiles] = useState(new Set());

  // State for referral email
  const [referralEmail, setReferralEmail] = useState('');

  // State for purchased profiles
  const [purchasedProfiles, setPurchasedProfiles] = useState([]);
  const [loadingPurchased, setLoadingPurchased] = useState(false);
  const [receiverId, setReceiverId] = useState('');
  const [locationNames, setLocationNames] = useState({});

  // Helper functions to convert IDs to names
  const getLocationName = (cityId, stateId, countryId) => {
    if (!cityId || !stateId || !countryId) {
      return 'N/A';
    }
    const key = `${cityId}-${stateId}-${countryId}`;
    return locationNames[key] || `${cityId}, ${stateId}, ${countryId}`;
  };

  const fetchLocationName = async (cityId, stateId, countryId) => {
    try {
      const key = `${cityId}-${stateId}-${countryId}`;
      
      // If we already have this location cached, return it
      if (locationNames[key]) {
        return locationNames[key];
      }

      const country = countries.find(c => c.id == countryId);
      
      // Get states for the country
      const statesData = await masterService.getStatesList(countryId);
      const state = statesData?.find(s => s.id == stateId);
      
      // Get cities for the state
      const citiesData = await masterService.getCitiesList(stateId);
      const city = citiesData?.find(c => c.id == cityId);
      
      const cityName = city ? city.name : cityId;
      const stateName = state ? state.name : stateId;
      const countryName = country ? country.name : countryId;
      
      const locationString = [cityName, stateName, countryName].filter(Boolean).join(', ');
      
      // Cache the result
      setLocationNames(prev => ({
        ...prev,
        [key]: locationString
      }));
      
      return locationString;
    } catch (error) {
      console.error('Error getting location names:', error);
      return `${cityId}, ${stateId}, ${countryId}`;
    }
  };

  const getCasteName = (casteId) => {
    const caste = castes.find(c => c.id == casteId);
    return caste ? caste.name : casteId;
  };

  const getRaasiName = (raasiId) => {
    const raasi = raasis.find(r => r.id == raasiId);
    return raasi ? raasi.name : raasiId;
  };

  const getNakshatraName = (nakshatraId) => {
    const nakshatra = nakshatras.find(n => n.id == nakshatraId);
    return nakshatra ? nakshatra.name : nakshatraId;
  };

  const getPadamName = (padamId) => {
    const padam = padams.find(p => p.id == padamId);
    return padam ? padam.name : padamId;
  };

  // Check if user is authenticated
  useEffect(() => {
    if (!currentUser && !isLoading) {
      console.log('No authenticated user found');
      // Check if there's a token in localStorage for gust users
      const token = localStorage.getItem('token');
      if (token) {
        console.log('Token found in localStorage:', token);
        // The AuthContext should handle token verification automatically
        // If we reach here, it means the token verification failed
        // and the user should be redirected to login
      }
    }
  }, [currentUser, isLoading]);

  // Update clock
  useEffect(() => {
    const updateClock = () => {
      const now = new Date();
      setCurrentTime(now.toLocaleTimeString('en-US'));
    };
    updateClock();
    const intervalId = setInterval(updateClock, 1000);
    return () => clearInterval(intervalId);
  }, []);

  // Load dropdown data on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const [
          countriesData,
          qualificationsData,
          raasisData,
          nakshatrasData,
          castesData,
        ] = await Promise.all([
          masterService.getCountryList(),
          masterService.getQualificationList(),
          masterService.getRaasiList(),
          masterService.getNakshatraList(),
          masterService.getCasteList(),
        ]);
        setCountries(countriesData || []);
        setQualifications(qualificationsData || []);
        setRaasis(raasisData || []);
        setNakshatras(nakshatrasData || []);
        setCastes(castesData || []);
      } catch (error) {
        console.error('Error loading data:', error);
        setError('Failed to load data');
      }
    };
    loadData();
  }, []);

  // Load purchased profiles on component mount
  useEffect(() => {
    const loadPurchasedProfiles = async () => {
      setLoadingPurchased(true);
      try {
        const profiles = await masterService.getProfilePurchasesDetails();
        setPurchasedProfiles(profiles);
      } catch (error) {
        console.error('Error loading purchased profiles:', error);
      } finally {
        setLoadingPurchased(false);
      }
    };

    // Only load if user is authenticated (has token)
    const token = localStorage.getItem('token');
    if (token) {
      loadPurchasedProfiles();
    }
  }, []);

  // Fetch location names for purchased profiles
  useEffect(() => {
    if (purchasedProfiles.length > 0 && countries.length > 0) {
      purchasedProfiles.forEach(purchase => {
        if (purchase.user?.city && purchase.user?.state && purchase.user?.country) {
          fetchLocationName(purchase.user.city, purchase.user.state, purchase.user.country);
        }
      });
    }
  }, [purchasedProfiles, countries]);

  // Clear form function
  const clearForm = () => {
    setSearchForm({
      age_min: '',
      age_max: '',
      location: '',
      qualification: '',
      gender: '',
      country: '',
      state: '',
      city: '',
      caste: '',
      raasi: '',
      nakshatra: '',
      padam: '',
      marital_status: '',
      religion: '',
      occupation: '',
      income: '',
    });
    setSearchResults([]);
    setSearched(false);
    setError("");
    setStates([]);
    setCities([]);
    setPadams([]);
  };

  // Handle form changes
  const handleFormChange = async (e) => {
    const { name, value } = e.target;
    setSearchForm((prev) => ({ ...prev, [name]: value }));
    if (name === 'country') {
      setSearchForm((prev) => ({ ...prev, state: '', city: '' }));
      setStates([]);
      setCities([]);
      if (value) {
        try {
          const statesData = await masterService.getStatesList(value);
          setStates(statesData || []);
        } catch (error) {
          console.error('Error loading states:', error);
        }
      }
    }
    if (name === 'state') {
      setSearchForm((prev) => ({ ...prev, city: '' }));
      setCities([]);
      if (value) {
        try {
          const citiesData = await masterService.getCitiesList(value);
          setCities(citiesData || []);
        } catch (error) {
          console.error('Error loading cities:', error);
        }
      }
    }
    if (name === 'nakshatra') {
      setSearchForm((prev) => ({ ...prev, padam: '' }));
      setPadams([]);
      if (value) {
        try {
          const padamsData = await masterService.getPadamList(value);
          setPadams(padamsData || []);
        } catch (error) {
          console.error('Error loading padams:', error);
        }
      }
    }
  };

  // Handle search
  const handleSearch = async () => {
    // Check for either currentUser or token (for gust users)
    const token = localStorage.getItem('token');
    if (!currentUser && !token) {
      setError('Please log in to search for profiles.');
      return;
    }

    // Check if at least one field is selected
    const hasAnyValue = Object.values(searchForm).some(value => value !== "");
    
    if (!hasAnyValue) {
      // Clear previous results when validation fails
      setSearchResults([]);
      setSearched(false);
      setError("Please select at least one search criteria (like age, location, education, etc.) to find your perfect match.");
      return;
    }

    // Clear previous results and error before starting new search
    setSearchResults([]);
    setError("");
    setSearched(true);
    setLoading(true);
    
    try {
      // Filter out empty values
      const searchParams = Object.fromEntries(
        Object.entries(searchForm).filter(([_, value]) => value !== '')
      );
      const results = await masterService.searchUsers(searchParams);
      console.log('Search results:', results);
      setSearchResults(results || []);
    } catch (error) {
      console.error('Search error:', error);
      setError('Failed to search users. Please try again.');
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle interest button click
  const handleInterestClick = () => {
    if (interestCount < INTEREST_LIMIT) {
      setInterestCount((prev) => prev + 1);
    } else {
      setShowLimitModal(true);
    }
  };

  // Handle shortlist button click
  const handleShortlistClick = (profileId) => {
    setShortlistedProfiles((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(profileId)) {
        newSet.delete(profileId);
      } else {
        newSet.add(profileId);
      }
      return newSet;
    });
  };

  // Handle referral submission
  const handleReferralSubmit = () => {
    // Check for either currentUser or token (for gust users)
    const token = localStorage.getItem('token');
    if (!currentUser && !token) {
      alert('Please log in to send referrals.');
      return;
    }
    
    if (referralEmail) {
      alert(`Referral sent to ${referralEmail}!`);
      setReferralEmail('');
    }
  };

  // Handle star rating
  const handleRating = (name, value) => {
    setRatings((prev) => ({ ...prev, [name]: value }));
  };

  // Handle modal close
  const handleCloseModal = () => {
    setShowPaywallModal(false);
    setShowLimitModal(false);
    setShowPurchasedModal(false);
    setShowPaywallModal2(false);
  };

  // Handle premium features
  const handlePremiumFeature = () => {
    setShowPaywallModal(true);
  };

  // Handle profile purchase
  const handlePurchase = async (receiverId) => {
    try {
      // Check if user is authenticated
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Please log in to purchase profiles.');
        return;
      }

      // Validate receiverId
      if (!receiverId) {
        alert('Please select a profile to purchase.');
        return;
      }

      const payload = {
        purpose: "Plan Subscription single profile",
        amount: 200,
        name: currentUser ? `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() || "Guest User" : "Guest User",
        email: currentUser?.email || "guest@example.com",
        phone: currentUser?.mobile || "0000000000",
        user_unique_id: currentUser?.unique_id || "GUEST",
        purchased_user_id: receiverId,
        redirect_type: "web",
      };

      const response = await masterService.createPayment(payload);
      console.log('Payment Response:', response);

      if (response.status === "success") {
        window.location.href = response.payment_url; // redirect to Instamojo
      } else {
        alert(`Failed to create payment: ${response.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error("Payment Error:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      if (error.response?.status === 401) {
        alert('Authentication failed. Please log in again.');
      } else if (error.response?.status === 400) {
        alert(`Bad request: ${error.response.data?.message || 'Invalid data'}`);
      } else if (error.response?.status === 500) {
        alert('Server error. Please try again later.');
      } else {
        alert(`Error creating payment: ${error.message || 'Unknown error'}`);
      }
    }
  };

  // Handle premium field access
  const handlePremiumFieldClick = (fieldName) => {
    setShowPremiumAccessModal(true);
  };

  // Calculate total amount spent on profiles
  const calculateTotalAmount = () => {
    return purchasedProfiles.reduce((total, purchase) => {
      const amount = parseFloat(purchase.amount) || 0;
      return total + amount;
    }, 0).toFixed(2);
  };

  // Field styling for search form
  const fieldSx = {
    borderRadius: '12px',
    backgroundColor: 'rgba(255,255,255,0.95)',
    minWidth: 240,
    maxWidth: 340,
    fontSize: '0.95rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    '& .MuiOutlinedInput-root': {
      '&:hover fieldset': {
        borderColor: '#4a1e28',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#4a1e28',
        borderWidth: '1px',
      },
    },
    '& .MuiInputLabel-root': {
      color: theme.palette.text.secondary,
      '&.Mui-focused': {
        color: '#4a1e28',
      },
    },
  };

  // Render select field helper
  const renderSelectField = (name, label, options, gridMd = 4) => (
    <Grid item xs={12} md={6} lg={gridMd}>
      <FormControl fullWidth sx={{ minWidth: 240, maxWidth: 340 }}>
        <InputLabel sx={{ color: theme.palette.text.secondary }}>
          {label}
        </InputLabel>
        <Select
          name={name}
          value={searchForm[name] || ''}
          onChange={handleFormChange}
          label={label}
          fullWidth
          sx={{
            ...fieldSx,
            py: 0.4,
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                minWidth: 240,
                maxWidth: 340,
                fontSize: '0.95rem',
                borderRadius: '12px',
                mt: 1,
                boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
              },
            },
          }}
        >
          <MenuItem value="">
            <em>Select {label}</em>
          </MenuItem>
          {options.map((option) => (
            <MenuItem
              key={option.id || option.value}
              value={option.id || option.value}
              sx={{ minWidth: 240, maxWidth: 240, fontSize: '0.95rem' }}
            >
              {option.name || option.value || option}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Grid>
  );

  // Render premium locked field helper
  const renderPremiumLockedField = (name, label, gridMd = 4) => (
    <Grid item xs={12} md={6} lg={gridMd}>
      <FormControl fullWidth sx={{ minWidth: 240, maxWidth: 340 }}>
        <InputLabel sx={{ color: '#6b7280' }}>
          {label} 🔒
        </InputLabel>
        <Select
          name={name}
          value=""
          disabled
          label={`${label} 🔒`}
          fullWidth
          onClick={() => handlePremiumFieldClick(name)}
          sx={{
            ...fieldSx,
            py: 0.4,
            bgcolor: '#f3f4f6',
            cursor: 'pointer',
            '&:hover': {
              bgcolor: '#e5e7eb',
            },
            '& .MuiSelect-icon': {
              color: '#6b7280',
            },
          }}
        >
          <MenuItem value="" disabled>
            <em>Premium Feature - Click to Unlock</em>
          </MenuItem>
        </Select>
      </FormControl>
    </Grid>
  );

  // Render star rating component
  const StarRating = ({ name, currentRating }) => {
    return (
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        {[1, 2, 3, 4, 5].map((value) => (
          <IconButton
            key={value}
            size="small"
            onClick={() => handleRating(name, value)}
            sx={{
              color: value <= currentRating ? '#facc15' : '#d1d5db',
              '&:hover': { color: '#facc15', transform: 'scale(1.2)' },
              p: 0.5,
              transition: 'all 0.2s ease-in-out',
            }}
            component={motion.button}
            whileHover={{ scale: 1.2 }}
            whileTap={{ scale: 0.9 }}
          >
            <StarIcon />
          </IconButton>
        ))}
      </Box>
    );
  };

  // Show loading while authentication is being verified
  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: '#f9fafb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Container maxWidth="sm" sx={{ textAlign: 'center', py: 8 }}>
          <CircularProgress size={60} sx={{ color: '#4a1e28', mb: 3 }} />
          <Typography variant="h5" sx={{ color: '#4a1e28' }}>
            Verifying authentication...
          </Typography>
        </Container>
      </Box>
    );
  }

  // Show authentication message after loading is complete
  const token = localStorage.getItem('token');
  if (!currentUser && !token) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: '#f9fafb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Container maxWidth="sm" sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h4" sx={{ mb: 2, color: '#4a1e28' }}>
            Please Log In
          </Typography>
          <Typography variant="body1" sx={{ color: '#6b7280', mb: 3 }}>
            You need to be logged in to access the Guest Dashboard.
          </Typography>
          <Button
            variant="contained"
            onClick={() => window.location.href = '/login-page'}
            sx={{
              bgcolor: '#4a1e28',
              '&:hover': { bgcolor: '#3b1720' },
            }}
          >
            Go to Login
          </Button>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f9fafb',
        pb: 24,
      }}
    >
      <Container maxWidth="lg" sx={{ py: 6 }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 4,
            borderBottom: '1px solid #e5e7eb',
            mb: 8,
          }}
          component={motion.div}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              color: '#4a1e28',
              fontSize: { xs: '1.8rem', md: '2.5rem' },
              letterSpacing: '-0.025em',
            }}
          >
          Dashboard
          </Typography>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#374151' }}>
              Welcome, {currentUser?.name || 'Guest User'}!
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: '#0d9488',
                fontFamily: 'monospace',
                letterSpacing: '0.05em',
              }}
            >
              {currentTime}
            </Typography>
            {/* Debug info */}
            {/* <Typography variant="caption" sx={{ color: '#6b7280' }}>
              Auth: {currentUser ? 'User' : 'Token'} | Token: {localStorage.getItem('token') ? 'Yes' : 'No'}
            </Typography> */}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151' }}>
                Your Rating
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: '#facc15',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                4.5 <StarIcon sx={{ fontSize: '1.2rem' }} />
              </Typography>
            </Box>
            <Box sx={{ position: 'relative' }}>
              <IconButton
                sx={{
                  color: '#4a1e28',
                  fontSize: '2rem',
                  cursor: 'not-allowed',
                  '&:hover': { bgcolor: '#f3f4f6' },
                }}
                disabled
              >
                <NotificationsIcon fontSize="large" />
              </IconButton>
              <Box
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  bgcolor: '#4a1e28',
                  color: 'white',
                  borderRadius: '50%',
                  width: 20,
                  height: 20,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.75rem',
                }}
              >
                <LockIcon sx={{ fontSize: '0.75rem' }} />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Main Content */}
        <Box sx={{ mb: 8 }}>
          {/* Activity & KPIs Section */}
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: '#4a1e28',
              mb: 4,
              fontSize: '1.5rem',
              letterSpacing: '-0.025em',
            }}
          >
            Your Activity & KPIs
          </Typography>
          <Grid container spacing={4} sx={{ mb: 8 }}>
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  p: 6,
                  borderRadius: 3,
                  background: 'white',
                  boxShadow: '0 6px 20px rgba(0,0,0,0.1)',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  transition: 'transform 0.3s ease-in-out',
                  '&:hover': { transform: 'translateY(-8px)' },
                }}
                component={motion.div}
                whileHover={{ scale: 1.02 }}
              >
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#6b7280', mb: 2 }}>
                    Profiles Purchased
                  </Typography>
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 800,
                      color: '#4a1e28',
                      mb: 2,
                      fontSize: { xs: '2.5rem', md: '3rem' },
                    }}
                  >
                   {purchasedProfiles.length}
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#6b7280' }}>
                    Total Spent: ₹{calculateTotalAmount()}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  onClick={() => setShowPurchasedModal(true)}
                  sx={{
                    mt: 4,
                    bgcolor: '#4a1e28',
                    color: 'white',
                    fontWeight: 600,
                    py: 2,
                    px: 4,
                    borderRadius: 2,
                    '&:hover': {
                      bgcolor: '#3b1720',
                    },
                    transition: 'all 0.3s ease',
                  }}
                  fullWidth
                >
                  View Details
                </Button>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  p: 6,
                  borderRadius: 3,
                  background: 'white',
                  boxShadow: '0 6px 20px rgba(0,0,0,0.1)',
                  height: '100%',
                  transition: 'transform 0.3s ease-in-out',
                  '&:hover': { transform: 'translateY(-8px)' },
                }}
                component={motion.div}
                whileHover={{ scale: 1.02 }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#6b7280', mb: 2 }}>
                  Refunds & Disputes
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 800,
                    color: '#4a1e28',
                    mb: 2,
                    fontSize: { xs: '2.5rem', md: '3rem' },
                  }}
                >
                  0
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ color: '#6b7280', mb: 1, fontSize: '0.75rem' }}>
                    • Refunds for no response (&gt;3 days) are reviewed case-by-case.
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6b7280', mb: 1, fontSize: '0.75rem' }}>
                    • Issues or arguments result in credits.
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.75rem' }}>
                    • Accounts may be paused after 2-3 disputes.
                  </Typography>
                </Box>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  p: 6,
                  borderRadius: 3,
                  background: 'white',
                  boxShadow: '0 6px 20px rgba(0,0,0,0.1)',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  transition: 'transform 0.3s ease-in-out',
                  '&:hover': { transform: 'translateY(-8px)' },
                }}
                component={motion.div}
                whileHover={{ scale: 1.02 }}
              >
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#6b7280', mb: 2 }}>
                    My Wallet
                  </Typography>
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 800,
                      color: '#4a1e28',
                      mb: 2,
                      fontSize: { xs: '2.5rem', md: '3rem' },
                    }}
                  >
                    ₹ 0
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#6b7280' }}>
                    Available Credits
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  sx={{
                    mt: 4,
                    bgcolor: '#facc15',
                    color: '#1f2937',
                    fontWeight: 600,
                    py: 2,
                    px: 4,
                    borderRadius: 2,
                    '&:hover': {
                      bgcolor: '#eab308',
                    },
                    transition: 'all 0.3s ease',
                  }}
                  fullWidth
                >
                  Credits & Promos
                </Button>
              </Card>
            </Grid>
          </Grid>

          {/* Main Grid Layout */}
          <Grid container spacing={4}>
            <Grid item xs={12} lg={8}>
              {/* Find Your Match Section */}
              <Card
                sx={{
                  p: 6,
                  borderRadius: 3,
                  background: 'white',
                  boxShadow: '0 6px 20px rgba(0,0,0,0.1)',
                  transition: 'transform 0.3s ease-in-out',
                  '&:hover': { transform: 'translateY(-8px)' },
                  mb: 4,
                }}
                component={motion.div}
                whileHover={{ scale: 1.02 }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: '#4a1e28',
                    mb: 4,
                    fontSize: '1.5rem',
                    letterSpacing: '-0.025em',
                  }}
                >
                  Find Your Match
                </Typography>
                <Grid container spacing={2}>
                  {/* Guest User - Basic 7 fields only */}
                  {renderSelectField(
                    'marital_status',
                    'Marital Status',
                    [
                      { id: 'Single', name: 'Single' },
                      { id: 'Married', name: 'Married' },
                      { id: 'Divorced', name: 'Divorced' },
                      { id: 'Widowed', name: 'Widowed' },
                    ],
                    4
                  )}
                  {renderSelectField(
                    'gender',
                    'Gender',
                    [
                      { id: 'male', name: 'Male' },
                      { id: 'female', name: 'Female' },
                    ],
                    4
                  )}
                  <Grid item xs={12} md={6} lg={3}>
                    <TextField
                      fullWidth
                      label="Min Age"
                      name="age_min"
                      type="number"
                      value={searchForm.age_min}
                      onChange={handleFormChange}
                      variant="outlined"
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} md={6} lg={3}>
                    <TextField
                      fullWidth
                      label="Max Age"
                      name="age_max"
                      type="number"
                      value={searchForm.age_max}
                      onChange={handleFormChange}
                      variant="outlined"
                      sx={fieldSx}
                    />
                  </Grid>
                  {renderSelectField('raasi', 'Raasi', raasis, 4)}
                  {renderSelectField('nakshatra', 'Nakshatra', nakshatras, 4)}
                  {renderSelectField('padam', 'Padam', padams, 4)}
                  {renderSelectField('caste', 'Caste/Community', castes, 4)}

                  {/* Premium Locked Fields */}
                  {renderPremiumLockedField('country', 'Country', 4)}
                  {renderPremiumLockedField('state', 'State', 4)}
                  {renderPremiumLockedField('city', 'City', 4)}
                  {renderPremiumLockedField('qualification', 'Education', 4)}
                  {renderPremiumLockedField('income', 'Income', 4)}
                  {renderPremiumLockedField('religion', 'Religion', 4)}
                  {renderPremiumLockedField('occupation', 'Occupation', 4)}
                </Grid>
                <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                  <Button
                    variant="contained"
                    onClick={handleSearch}
                    disabled={loading}
                    sx={{
                      flex: 1,
                      bgcolor: '#4a1e28',
                      color: 'white',
                      fontWeight: 700,
                      py: 1.5,
                      px: 3,
                      borderRadius: 2,
                      '&:hover': {
                        bgcolor: '#3b1720',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      'Search Profiles'
                    )}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={clearForm}
                    disabled={loading}
                    sx={{
                      flex: 1,
                      borderColor: '#4a1e28',
                      color: '#4a1e28',
                      fontWeight: 600,
                      py: 1.5,
                      px: 3,
                      borderRadius: 2,
                      '&:hover': {
                        borderColor: '#3b1720',
                        bgcolor: '#f9f9f9',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    Clear
                  </Button>
                </Box>
              </Card>

              {/* Error Alert */}
              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              {/* Search Results Section */}
              {searched && (
                <Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      color: '#4a1e28',
                      mb: 4,
                      fontSize: '1.5rem',
                      letterSpacing: '-0.025em',
                    }}
                  >
                    Search Results
                  </Typography>
                  <Grid container spacing={3}>
                    {searchResults.length > 0 ? (
                      searchResults.map((profile, index) => (
                        <Grid item xs={12} md={6} key={profile.id || index}>
                          <Card
                            sx={{
                              borderRadius: 3,
                              boxShadow: '0 6px 20px rgba(0,0,0,0.1)',
                              overflow: 'hidden',
                              transition: 'transform 0.3s ease-in-out',
                              '&:hover': { transform: 'translateY(-8px)' },
                            }}
                            component={motion.div}
                            whileHover={{ scale: 1.02 }}
                          >
                            {/* Profile Image with proper loading pattern and fallback */}
                            <Box sx={{ position: 'relative' }}>
                              {profile.image ? (
                                <img
                                  src={`${process.env.REACT_APP_IMAGE_BASE_URL}/${profile.image}`}
                                  alt={`${profile.first_name || profile.name || 'Unknown'}'s Profile Picture`}
                                  style={{
                                    width: '100%',
                                    height: '192px',
                                    objectFit: 'cover',
                                  }}
                                  onError={(e) => {
                                    // Hide the image and show fallback avatar if image fails to load
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                              ) : null}
                              
                              {/* Fallback Avatar with first letter of name */}
                              <Box
                                sx={{
                                  display: profile.image ? 'none' : 'flex',
                                  width: '100%',
                                  height: '192px',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  bgcolor: index % 2 === 0 ? '#4a1e28' : '#0d9488',
                                  color: 'white',
                                  fontSize: '4rem',
                                  fontWeight: 700,
                                  textTransform: 'uppercase',
                                }}
                              >
                                {(profile.first_name || profile.name || 'U').charAt(0)}
                              </Box>
                            </Box>
                            <Box sx={{ p: 2 }}>
                              <Typography variant="h5" sx={{ fontWeight: 700, color: '#1f2937' }}>
                                {profile.first_name || profile.name || 'Unknown'}, {profile.age || 'N/A'}
                              </Typography>
                              <Box sx={{ position: 'relative', mt: 2 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: '#6b7280',
                                    filter: 'blur(4px)',
                                    userSelect: 'none',
                                  }}
                                >
                                  {profile.bio || profile.description || 'Profile description is blurred for guest users. Upgrade to view full details...'}
                                </Typography>
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: 'rgba(255,255,255,0.7)',
                                  }}
                                >
                                  <Button
                                    variant="contained"
                                    onClick={() => {
                                      setReceiverId(profile.unique_id);
                                      handlePremiumFeature();
                                    }}
                                    sx={{
                                      bgcolor: '#facc15',
                                      color: '#1f2937',
                                      fontWeight: 700,
                                      py: 1,
                                      px: 2.5,
                                      borderRadius: 3,
                                      '&:hover': {
                                        bgcolor: '#eab308',
                                      },
                                      transition: 'all 0.3s ease',
                                    }}
                                    component={motion.button}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                  >
                                    View Full Profile 🔒
                                  </Button>
                                </Box>
                              </Box>
                            </Box>
                            <Box sx={{ p: 2, borderTop: '1px solid #e5e7eb' }}>
                              <Grid container spacing={1}>
                                <Grid item xs={4}>
                                  <Button
                                    variant={shortlistedProfiles.has(profile.id || index) ? 'contained' : 'outlined'}
                                    onClick={() => handleShortlistClick(profile.id || index)}
                                    sx={{
                                      borderColor: shortlistedProfiles.has(profile.id || index) ? '#4a1e28' : '#e5e7eb',
                                      bgcolor: shortlistedProfiles.has(profile.id || index) ? '#4a1e28' : 'transparent',
                                      color: shortlistedProfiles.has(profile.id || index) ? 'white' : '#1f2937',
                                      borderRadius: 3,
                                      py: 1,
                                      px: 1.5,
                                      fontSize: '0.875rem',
                                      '&:hover': {
                                        borderColor: shortlistedProfiles.has(profile.id || index) ? '#3b1720' : '#4a1e28',
                                        bgcolor: shortlistedProfiles.has(profile.id || index) ? '#3b1720' : 'transparent',
                                      },
                                      transition: 'all 0.3s ease',
                                    }}
                                    fullWidth
                                    component={motion.button}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                  >
                                    ❤️ Shortlist
                                  </Button>
                                </Grid>
                                <Grid item xs={4}>
                                  <Button
                                    variant="outlined"
                                    onClick={handleInterestClick}
                                    sx={{
                                      borderColor: '#e5e7eb',
                                      color: '#1f2937',
                                      borderRadius: 3,
                                      py: 1,
                                      px: 1.5,
                                      fontSize: '0.875rem',
                                      '&:hover': {
                                        borderColor: '#4a1e28',
                                      },
                                      transition: 'all 0.3s ease',
                                    }}
                                    fullWidth
                                    component={motion.button}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                  >
                                    👋 Interest ({interestCount}/{INTEREST_LIMIT})
                                  </Button>
                                </Grid>
                                <Grid item xs={4}>
                                  <Button
                                    variant="outlined"
                                    onClick={handlePremiumFeature}
                                    disabled
                                    sx={{
                                      bgcolor: '#f3f4f6',
                                      color: '#6b7280',
                                      borderRadius: 3,
                                      py: 1,
                                      px: 1.5,
                                      fontSize: '0.875rem',
                                      borderColor: '#e5e7eb',
                                    }}
                                    fullWidth
                                  >
                                    💬 Chat 🔒
                                  </Button>
                                </Grid>
                              </Grid>
                            </Box>
                          </Card>
                        </Grid>
                      ))
                    ) : (
                      <Grid item xs={12}>
                        <Card sx={{ p: 4, textAlign: 'center', bgcolor: 'white', boxShadow: '0 6px 20px rgba(0,0,0,0.1)' }}>
                          <Typography variant="h6" color="textSecondary">
                            No profiles found matching your criteria. Try adjusting your search filters.
                          </Typography>
                        </Card>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}
            </Grid>

            {/* Sidebar */}
            <Grid item xs={12} lg={4}>
              <Grid container spacing={3}>
                {/* Rate Your Interactions */}
                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      background: 'white',
                      boxShadow: '0 6px 20px rgba(0,0,0,0.1)',
                      transition: 'transform 0.3s ease-in-out',
                      '&:hover': { transform: 'translateY(-8px)' },
                      height: '100%',
                    }}
                    component={motion.div}
                    whileHover={{ scale: 1.02 }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: '#4a1e28',
                        mb: 2,
                        fontSize: '1.1rem',
                        letterSpacing: '-0.025em',
                      }}
                    >
                      Rate Your Interactions
                    </Typography>
                    <Box sx={{ space: 2 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          p: 1.5,
                          bgcolor: '#f9fafb',
                          borderRadius: 2,
                          mb: 2,
                          transition: 'background 0.3s ease',
                          '&:hover': { bgcolor: '#f1f5f9' },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar
                            sx={{ 
                              width: 40, 
                              height: 40, 
                              transition: 'transform 0.3s ease', 
                              '&:hover': { transform: 'scale(1.1)' },
                              bgcolor: '#4a1e28'
                            }}
                          >
                            P
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1f2937' }}>
                              Priya, 28
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#6b7280' }}>
                              Aug 15, 2025
                            </Typography>
                          </Box>
                        </Box>
                        <StarRating name="priya" currentRating={ratings.priya} />
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          p: 1.5,
                          bgcolor: '#f9fafb',
                          borderRadius: 2,
                          transition: 'background 0.3s ease',
                          '&:hover': { bgcolor: '#f1f5f9' },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar
                            sx={{ 
                              width: 40, 
                              height: 40, 
                              transition: 'transform 0.3s ease', 
                              '&:hover': { transform: 'scale(1.1)' },
                              bgcolor: '#0d9488'
                            }}
                          >
                            A
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1f2937' }}>
                              Arjun, 31
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#6b7280' }}>
                              Aug 12, 2025
                            </Typography>
                          </Box>
                        </Box>
                        <StarRating name="arjun" currentRating={ratings.arjun} />
                      </Box>
                    </Box>
                  </Card>
                </Grid>

                {/* Your Guest Access */}
                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      background: 'white',
                      boxShadow: '0 6px 20px rgba(0,0,0,0.1)',
                      transition: 'transform 0.3s ease-in-out',
                      '&:hover': { transform: 'translateY(-8px)' },
                      height: '100%',
                    }}
                    component={motion.div}
                    whileHover={{ scale: 1.02 }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: '#4a1e28',
                        mb: 2,
                        fontSize: '1.1rem',
                        letterSpacing: '-0.025em',
                      }}
                    >
                      Your Guest Access
                    </Typography>
                    <Box component="ul" sx={{ space: 1, listStyle: 'none', p: 0, m: 0 }}>
                      <Box component="li" sx={{ display: 'flex', alignItems: 'center', mb: 1, fontSize: '0.875rem' }}>
                        <span style={{ marginRight: '0.5rem', fontSize: '1rem' }}>✅</span>
                        Search with 8 basic filters
                      </Box>
                      <Box component="li" sx={{ display: 'flex', alignItems: 'center', mb: 1, fontSize: '0.75rem', color: '#6b7280', pl: 2 }}>
                        • Marital Status, Gender, Age Range
                      </Box>
                      <Box component="li" sx={{ display: 'flex', alignItems: 'center', mb: 1, fontSize: '0.75rem', color: '#6b7280', pl: 2 }}>
                        • Raasi, Nakshatra, Padam, Caste
                      </Box>
                      <Box component="li" sx={{ display: 'flex', alignItems: 'center', mb: 1, fontSize: '0.875rem' }}>
                        <span style={{ marginRight: '0.5rem', fontSize: '1rem' }}>✅</span>
                        Shortlist profiles
                      </Box>
                      <Box component="li" sx={{ display: 'flex', alignItems: 'center', mb: 1, fontSize: '0.875rem' }}>
                        <span style={{ marginRight: '0.5rem', fontSize: '1rem' }}>✅</span>
                        Express Interest (3 per day)
                      </Box>
                      <Box component="li" sx={{ display: 'flex', alignItems: 'center', mb: 1, fontSize: '0.875rem', color: '#6b7280' }}>
                        <span style={{ marginRight: '0.5rem', fontSize: '1rem' }}>🔒</span>
                        View Full Profiles
                      </Box>
                      <Box component="li" sx={{ display: 'flex', alignItems: 'center', fontSize: '0.875rem', color: '#6b7280' }}>
                        <span style={{ marginRight: '0.5rem', fontSize: '1rem' }}>🔒</span>
                        Chat / Contact details
                      </Box>
                    </Box>
                  </Card>
                </Grid>

                {/* Refer a Friend */}
                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      background: 'white',
                      boxShadow: '0 6px 20px rgba(0,0,0,0.1)',
                      transition: 'transform 0.3s ease-in-out',
                      '&:hover': { transform: 'translateY(-8px)' },
                      height: '100%',
                    }}
                    component={motion.div}
                    whileHover={{ scale: 1.02 }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: '#4a1e28',
                        mb: 2,
                        fontSize: '1.1rem',
                        letterSpacing: '-0.025em',
                      }}
                    >
                      Refer a Friend
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6b7280', mb: 3, lineHeight: 1.4, fontSize: '0.875rem' }}>
                      You both get a ₹200 coupon when they make their first purchase!
                    </Typography>
                    <TextField
                      fullWidth
                      type="email"
                      placeholder="Enter friend's email"
                      value={referralEmail}
                      onChange={(e) => setReferralEmail(e.target.value)}
                      size="small"
                      sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': { borderColor: '#4a1e28' },
                          '&.Mui-focused fieldset': { borderColor: '#4a1e28' },
                        },
                      }}
                    />
                    <Button
                      variant="contained"
                      onClick={handleReferralSubmit}
                      sx={{
                        bgcolor: '#facc15',
                        color: '#1f2937',
                        fontWeight: 600,
                        py: 1.5,
                        px: 2,
                        borderRadius: 2,
                        fontSize: '0.875rem',
                        '&:hover': {
                          bgcolor: '#eab308',
                        },
                        transition: 'all 0.3s ease',
                      }}
                      fullWidth
                      component={motion.button}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      Send Referral
                    </Button>
                  </Card>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            bgcolor: '#4a1e28',
            color: 'white',
            p: 4,
            zIndex: 10,
            boxShadow: '0 -6px 20px rgba(0,0,0,0.15)',
          }}
        >
          <Container maxWidth="lg">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  textAlign: { xs: 'center', md: 'left' },
                  letterSpacing: '0.025em',
                }}
              >
                Upgrade to unlock full features and find your match faster!
              </Typography>
              <Link to="/purchase" style={{ textDecoration: 'none' }}>
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: '#facc15',
                    color: '#1f2937',
                    fontWeight: 600,
                    py: 1.5,
                    px: 8,
                    borderRadius: 3,
                    '&:hover': {
                      bgcolor: '#eab308',
                    },
                    transition: 'all 0.3s ease',
                  }}
                  component={motion.button}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Upgrade to Premium
                </Button>
              </Link>
            </Box>
          </Container>
        </Box>

        {/* Purchased Profiles Modal */}
        {showPurchasedModal && (
          <Box
            sx={{
              position: 'fixed',
              inset: 0,
              bgcolor: 'rgba(0,0,0,0.75)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 50,
            }}
            onClick={handleCloseModal}
            component={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Card
              sx={{
                maxWidth: 'md',
                width: '90%',
                maxHeight: '80vh',
                overflow: 'auto',
                p: 6,
                borderRadius: 3,
                background: 'white',
                boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
              }}
              onClick={(e) => e.stopPropagation()}
              component={motion.div}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  color: '#4a1e28',
                  mb: 4,
                  textAlign: 'center',
                  fontSize: { xs: '1.8rem', md: '2.5rem' },
                  letterSpacing: '-0.025em',
                }}
              >
                Your Purchased Profiles ({purchasedProfiles.length})
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={async () => {
                    const token = localStorage.getItem('token');
                    if (token) {
                      setLoadingPurchased(true);
                      try {
                        const profiles = await masterService.getProfilePurchasesDetails();
                        setPurchasedProfiles(profiles);
                      } catch (error) {
                        console.error('Error refreshing:', error);
                      } finally {
                        setLoadingPurchased(false);
                      }
                    }
                  }}
                  sx={{
                    borderColor: '#4a1e28',
                    color: '#4a1e28',
                    '&:hover': { borderColor: '#3b1720' },
                  }}
                >
                  Refresh
                </Button>
              </Box>
              

              {loadingPurchased ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={32} />
                </Box>
              ) : purchasedProfiles.length > 0 ? (
                <Grid container spacing={3} sx={{ mb: 4 , mt:4}}>
                  {purchasedProfiles.map((purchase, index) => (
                    <Grid item xs={12} key={index}>
                      <Card
                        sx={{
                          borderRadius: 3,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                          p: 3,
                          bgcolor: '#f9fafb',
                        }}
                        component={motion.div}
                        whileHover={{ scale: 1.01 }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Avatar
                            src={
                              purchase.user?.image
                                ? `${process.env.REACT_APP_IMAGE_BASE_URL}/${purchase.user.image}`
                                : undefined
                            }
                            sx={{
                              width: 60,
                              height: 60,
                              mr: 2,
                              transition: 'transform 0.3s ease',
                              '&:hover': { transform: 'scale(1.1)' },
                              bgcolor: purchase.user?.image ? 'transparent' : '#4a1e28',
                            }}
                            alt={purchase.user?.first_name || purchase.user?.name || 'User Avatar'}
                          >
                            {!purchase.user?.image && (purchase.user?.first_name?.charAt(0) || purchase.user?.name?.charAt(0) || 'U')}
                          </Avatar>
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937' }}>
                              {purchase.user?.first_name || purchase.user?.name || 'Unknown User'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#0d9488', fontWeight: 600 }}>
                              Purchased for ₹{purchase.amount}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ pl: 2, mb: 2 }}>
                          <Typography variant="body2" sx={{ color: '#374151', mb: 1 }}>
                            <strong>Age:</strong> {purchase.user?.age || 'N/A'}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#374151', mb: 1 }}>
                            <strong>Gender:</strong> {purchase.user?.gender ? purchase.user.gender.charAt(0).toUpperCase() + purchase.user.gender.slice(1) : 'N/A'}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#374151', mb: 1 }}>
                            <strong>Location:</strong> {getLocationName(purchase.user?.city, purchase.user?.state, purchase.user?.country)}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#374151', mb: 1 }}>
                            <strong>Caste:</strong> {getCasteName(purchase.user?.caste  || 'N/A')}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#374151', mb: 1 }}>
                            <strong>Raasi:</strong> {getRaasiName(purchase.user?.raasi  || 'N/A')}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#374151', mb: 1 }}>
                            <strong>Nakshatra:</strong> {getNakshatraName(purchase.user?.nakshatra  || 'N/A')}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#374151' }}>
                            <strong>Bio:</strong> {purchase.user?.bio || 'No bio available'}
                          </Typography>
                        </Box>
                        <Box sx={{ pl: 2 }}>
                          <Grid container spacing={1}>
                            <Grid item xs={6}>
                              </Grid>
                              <Grid item xs={6}>
                                <Button
                                  variant="contained"
                                  onClick={() => {
                                    // Show profile details in an alert or modal
                                    const profile = purchase.user;
                                    const details = `
                                            Contact Details:
                                            Name: ${profile?.first_name || profile?.name || 'N/A'}
                                            Gender: ${profile?.gender ? profile.gender.charAt(0).toUpperCase() + profile.gender.slice(1) : 'N/A'}
                                            Phone: ${profile?.mobile || 'N/A'}
                                            Email: ${profile?.email || 'N/A'}
                                  `;
                                    alert(details);
                                  }}
                                  sx={{
                                    bgcolor: '#4a1e28',
                                    color: 'white',
                                    borderRadius: 3,
                                    py: 1,
                                    px: 1.5,
                                    fontSize: '0.875rem',
                                    '&:hover': {
                                      bgcolor: '#3b1720',
                                    },
                                    transition: 'all 0.3s ease',
                                  }}
                                  fullWidth
                                  startIcon={<ContactPhoneIcon />}
                                  component={motion.button}
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                >
                                  Contact Details
                                </Button>
                              </Grid>
                            </Grid>
                        </Box>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="h6" sx={{ color: '#6b7280', mb: 2 }}>
                    No Profiles Purchased Yet
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                    Start exploring profiles to make your first purchase!
                  </Typography>
                </Box>
              )}
              <Box sx={{ textAlign: 'center' }}>
                <Button
                  variant="outlined"
                  onClick={handleCloseModal}
                  sx={{
                    borderColor: '#4a1e28',
                    color: '#4a1e28',
                    fontWeight: 600,
                    py: 1.5,
                    px: 4,
                    borderRadius: 2,
                    '&:hover': {
                      borderColor: '#3b1720',
                      bgcolor: '#4a1e28',
                      color: 'white',
                    },
                    transition: 'all 0.3s ease',
                  }}
                  component={motion.button}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Close
                </Button>
              </Box>
            </Card>
          </Box>
        )}

        {/* Paywall Modal */}
        {showPaywallModal && (
          <Box
            sx={{
              position: 'fixed',
              inset: 0,
              bgcolor: 'rgba(0,0,0,0.75)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 50,
            }}
            onClick={handleCloseModal}
            component={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Card
              sx={{
                maxWidth: 'md',
                width: '90%',
                p: 6,
                borderRadius: 3,
                textAlign: 'center',
                background: 'white',
                boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
              }}
              onClick={(e) => e.stopPropagation()}
              component={motion.div}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  color: '#4a1e28',
                  mb: 2,
                  fontSize: { xs: '1.8rem', md: '2.5rem' },
                  letterSpacing: '-0.025em',
                }}
              >
                Unlock Premium Access
              </Typography>
              <Typography variant="body1" sx={{ color: '#6b7280', mb: 4, lineHeight: 1.6 }}>
                Upgrade your account to view full profiles and start conversations.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/purchase" style={{ textDecoration: 'none' }}>
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: '#facc15',
                    color: '#1f2937',
                    fontWeight: 700,
                    py: 1,
                    px: 2.5,
                    borderRadius: 2,
                    fontSize: '1rem',
                    minWidth: '120px',
                    '&:hover': {
                      bgcolor: '#eab308',
                    },
                    transition: 'all 0.3s ease',
                  }}
                  component={motion.button}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Upgrade Now
                </Button>
                </Link>
                <Button
                  variant="contained"
                  onClick={() => handlePurchase(receiverId)}
                  disabled={!receiverId}
                  sx={{
                    bgcolor: receiverId ? '#4a1e28' : '#9ca3af',
                    color: 'white',
                    fontWeight: 700,
                    py: 1,
                    px: 2.5,
                    borderRadius: 2,
                    fontSize: '1rem',
                    minWidth: '120px',
                    '&:hover': {
                      bgcolor: receiverId ? '#3b1720' : '#9ca3af',
                    },
                    transition: 'all 0.3s ease',
                  }}
                  component={motion.button}
                  whileHover={{ scale: receiverId ? 1.05 : 1 }}
                  whileTap={{ scale: receiverId ? 0.95 : 1 }}
                >
                  {receiverId ? `Purchase Profile / ₹200` : 'Select Profile First'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleCloseModal}
                  sx={{
                    borderColor: '#4a1e28',
                    color: '#4a1e28',
                    fontWeight: 600,
                    py: 1,
                    px: 2.5,
                    borderRadius: 2,
                    fontSize: '1rem',
                    minWidth: '120px',
                    '&:hover': {
                      borderColor: '#3b1720',
                      bgcolor: '#4a1e28',
                      color: 'white',
                    },
                    transition: 'all 0.3s ease',
                  }}
                  component={motion.button}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Maybe Later
                </Button>
              </Box>
            </Card>
          </Box>
        )}

        {showPaywallModal2 && (
          <Box
            sx={{
              position: 'fixed',
              inset: 0,
              bgcolor: 'rgba(0,0,0,0.75)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 50,
            }}
            onClick={handleCloseModal}
            component={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Card
              sx={{
                maxWidth: 'md',
                width: '90%',
                p: 6,
                borderRadius: 3,
                textAlign: 'center',
                background: 'white',
                boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
              }}
              onClick={(e) => e.stopPropagation()}
              component={motion.div}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  color: '#4a1e28',
                  mb: 2,
                  fontSize: { xs: '1.8rem', md: '2.5rem' },
                  letterSpacing: '-0.025em',
                }}
              >
                Unlock Premium Access
              </Typography>
              <Typography variant="body1" sx={{ color: '#6b7280', mb: 4, lineHeight: 1.6 }}>
                Upgrade your account to view full profiles and start conversations.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Link to="/purchase" style={{ textDecoration: 'none' }}>
                  <Button
                    variant="contained"
                    sx={{
                      bgcolor: '#facc15',
                      color: '#1f2937',
                      fontWeight: 700,
                      py: 1,
                      px: 2.5,
                      borderRadius: 2,
                      fontSize: '1rem',
                      minWidth: '120px',
                      '&:hover': {
                        bgcolor: '#eab308',
                      },
                      transition: 'all 0.3s ease',
                    }}
                    component={motion.button}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Upgrade Now
                  </Button>
                </Link>
                
                <Button
                  variant="outlined"
                  onClick={handleCloseModal}
                  sx={{
                    borderColor: '#4a1e28',
                    color: '#4a1e28',
                    fontWeight: 600,
                    py: 1,
                    px: 2.5,
                    borderRadius: 2,
                    fontSize: '1rem',
                    minWidth: '120px',
                    '&:hover': {
                      borderColor: '#3b1720',
                      bgcolor: '#4a1e28',
                      color: 'white',
                    },
                    transition: 'all 0.3s ease',
                  }}
                  component={motion.button}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Maybe Later
                </Button>
              </Box>
            </Card>
          </Box>
        )}

        {/* Limit Modal */}
        {showLimitModal && (
          <Box
            sx={{
              position: 'fixed',
              inset: 0,
              bgcolor: 'rgba(0,0,0,0.75)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 50,
            }}
            onClick={handleCloseModal}
            component={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Card
              sx={{
                maxWidth: 'md',
                width: '90%',
                p: 6,
                borderRadius: 3,
                textAlign: 'center',
                background: 'white',
                boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
              }}
              onClick={(e) => e.stopPropagation()}
              component={motion.div}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  color: '#4a1e28',
                  mb: 2,
                  fontSize: { xs: '1.8rem', md: '2.5rem' },
                  letterSpacing: '-0.025em',
                }}
              >
                Daily Limit Reached
              </Typography>
              <Typography variant="body1" sx={{ color: '#6b7280', mb: 4, lineHeight: 1.6 }}>
                You've reached today's interest limit of 3. Upgrade for unlimited interests!
              </Typography>
              <Button
                variant="contained"
                sx={{
                  bgcolor: '#facc15',
                  color: '#1f2937',
                  fontWeight: 700,
                  py: 1.5,
                  px: 3,
                  borderRadius: 2,
                  fontSize: '1.125rem',
                  '&:hover': {
                    bgcolor: '#eab308',
                  },
                  transition: 'all 0.3s ease',
                }}
                fullWidth
                component={motion.button}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                See Premium Plans
              </Button>
              <Button
                variant="text"
                onClick={handleCloseModal}
                sx={{
                  mt: 1.5,
                  color: '#6b7280',
                  '&:hover': {
                    bgcolor: 'transparent',
                    color: '#4a1e28',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Close
              </Button>
            </Card>
          </Box>
        )}

        {/* Premium Access Modal */}
        {showPremiumAccessModal && (
          <Box
            sx={{
              position: 'fixed',
              inset: 0,
              bgcolor: 'rgba(0,0,0,0.75)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 50,
            }}
            onClick={() => setShowPremiumAccessModal(false)}
            component={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Card
              sx={{
                maxWidth: 500,
                width: '90%',
                p: 6,
                borderRadius: 3,
                textAlign: 'center',
                background: 'white',
                boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
              }}
              onClick={(e) => e.stopPropagation()}
              component={motion.div}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              {/* Lock Icon */}
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  bgcolor: '#4a1e28',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                }}
              >
                <LockIcon sx={{ fontSize: 40, color: 'white' }} />
              </Box>

              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: '#4a1e28',
                  mb: 2,
                  fontSize: { xs: '1.5rem', md: '2rem' },
                  letterSpacing: '-0.025em',
                }}
              >
                Premium Features Locked
              </Typography>

              <Typography variant="body1" sx={{ color: '#6b7280', mb: 4, lineHeight: 1.6 }}>
                Unlock advanced search filters including Nakshatra, Caste, and Raasi to find your perfect match with deeper astrological compatibility.
              </Typography>

              {/* Premium Features List */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ color: '#4a1e28', mb: 2, fontWeight: 600 }}>
                  Premium Features Include:
                </Typography>
                <Box sx={{ textAlign: 'left', maxWidth: 300, mx: 'auto' }}>
                  {[
                    '🔒 Nakshatra-based matching',
                    '🔒 Caste compatibility filters',
                    '🔒 Raasi (Zodiac sign) filters',
                    '🔒 Advanced astrological insights',
                    '🔒 Priority profile recommendations'
                  ].map((feature, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 1,
                        p: 1,
                        bgcolor: '#f8fafc',
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant="body2" sx={{ color: '#374151' }}>
                        {feature}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  onClick={() => {
                    setShowPremiumAccessModal(false);
                    setShowPaywallModal2(true);
                  }}
                  sx={{
                    bgcolor: '#4a1e28',
                    color: 'white',
                    fontWeight: 700,
                    py: 1.5,
                    px: 4,
                    borderRadius: 2,
                    '&:hover': {
                      bgcolor: '#3b1720',
                    },
                    transition: 'all 0.3s ease',
                  }}
                  component={motion.button}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Upgrade to Premium
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setShowPremiumAccessModal(false)}
                  sx={{
                    borderColor: '#4a1e28',
                    color: '#4a1e28',
                    fontWeight: 600,
                    py: 1.5,
                    px: 4,
                    borderRadius: 2,
                    '&:hover': {
                      borderColor: '#3b1720',
                      bgcolor: '#4a1e28',
                      color: 'white',
                    },
                    transition: 'all 0.3s ease',
                  }}
                  component={motion.button}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Maybe Later
                </Button>
              </Box>
            </Card>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default GuestUserDashboard;